// PRD 01 — Home Dashboard (홈 대시보드). Read-only aggregates + time-series charts.
// FE-proposed wire shapes (BE unconfirmed) — see api-contracts/home.md.

// Chart period tabs (PRD 01 §3.2): weekly = 7 days (M/D), monthly = 6 months (YYYY.MM).
export type DashboardPeriod = 'weekly' | 'monthly';

export const DASHBOARD_PERIODS: readonly DashboardPeriod[] = ['weekly', 'monthly'] as const;

// 3 summary cards (PRD 01 §3.1, v1.3 — "PRO 개발 현황"·"참고자료 개발 현황" removed, SHCT-831).
// Explicit fields rather than generic breakdown arrays so display labels (의사/간호사/CRC, ...)
// stay in i18n, not the wire payload. The server still sends the two dropped aggregates; they are
// simply not declared here, so nothing reads them.
export interface DashboardStats {
  // Data last-refresh time (ISO 8601 UTC). UI formats as `YYYY. M. DD. HH:MM`.
  // NOTE: comes from the API field — NOT the client clock.
  updatedAt: string;
  // 전체 환자: total + the 관리중 / 관리종료 / 탈퇴 split (PRD §3.1, clarification ADM.027).
  // `total` is counted independently of the three parts, so the parts summing to it is an
  // assertion worth testing rather than something the shape guarantees.
  totalPatients: { total: number; active: number; closed: number; withdrawn: number };
  // 전체 의료진: total + 직군별 (의사/간호사/CRC). REGISTERED accounts only — 미가입 and 탈퇴
  // are excluded server-side (ADM.027), so every figure here already reflects that.
  totalStaff: { total: number; doctor: number; nurse: number; crc: number };
  // 문의/관리 현황: 환자문의 single type → today / cumulative (오늘 / 누적).
  inquiries: { today: number; cumulative: number };
}

// One chart series. `color` is a CSS custom-property reference (e.g. 'var(--chart-1)')
// resolved at render time — mirrors the prototype's color tokens.
export interface DashboardSeries {
  label: string;
  data: number[];
  color: string;
}

export interface DashboardChart {
  labels: string[];
  datasets: DashboardSeries[];
}

// Two charts returned together per period (PRD 01 §3.2):
//  - patientsByDepartment: stacked bar per 주진단(질병군) + "전체 환자수" line overlay (dual Y-axis,
//    FE-derived line). The FIELD NAME still says department because it is the API contract and renaming it
//    would break every client at once; since ADM.028 / SHCT-506 the server fills it with disease groups,
//    with an appended 미분류 series for patients who have no 주진단 recorded.
//  - inquiryStatus: single-series bar (환자문의).
export interface DashboardChartBundle {
  patientsByDepartment: DashboardChart;
  inquiryStatus: DashboardChart;
}

// Aligns to the repo error standard (CLAUDE.md §7.1): typed { code, message }.
// NETWORK = no response (offline); UNKNOWN = unmapped; INTERNAL_ERROR = server 5xx.
export type DashboardApiErrorCode =
  | 'NETWORK'
  | 'UNAUTHENTICATED'
  | 'INTERNAL_ERROR'
  | 'UNKNOWN';

export class DashboardApiError extends Error {
  constructor(
    public readonly code: DashboardApiErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'DashboardApiError';
  }
}

export interface DashboardApi {
  getSummary(): Promise<DashboardStats>;
  getCharts(period: DashboardPeriod): Promise<DashboardChartBundle>;
}
